/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ReactElement,
  useEffect,
  useImperativeHandle,
  FC,
  useRef,
  useState,
  useCallback,
  CSSProperties,
  useMemo,
} from "react";
import {
  Column,
  Row,
  TableRowProps,
  useFlexLayout,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import {
  TableData,
  TableDataAction,
  TableSection,
  TableSortKey,
} from "../../types/components/datatable";
import { Draggable, DropTarget } from "../DragAndDrop";
import { v4 as uuid } from "uuid";
import {
  getDragItemStyle,
  getKeyFromCol,
  getLockedString,
} from "../../utils/utils";
import { TableFilterModal } from "./TableFilterModal";
import { TableLoading } from "./TableLoading";
import { NoMessageData } from "./NoMessageData";
import { LockRecord } from "./LockRecord";
import { BigText } from "../Displays/BigText";
// icons
import RushIcon from "../../assets/icons/rush-mask.svg";
import {
  SortDarkIcon,
  SortDarkAcsIcon,
  SortDarkDescIcon,
  SortLightAcsIcon,
  SortLightDescIcon,
  SortLightIcon,
} from "../Icons";

export const COLUMNS_IGNORE_IDS = ["selection", "action"];

interface IndeterminateCheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  isRushIcon?: boolean;
  isCRIcon?: boolean;
  crData?: {
    createdBy: string;
    createdAt: string;
    findings?: string[];
    comment?: string;
    acknowledgedAt?: string;
  };
  renderCRIcon?: (crData?: any) => React.ReactNode;
  indeterminate?: boolean;
}

// @ts-ignore
const IndeterminateCheckbox = React.forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(
  // eslint-disable-next-line react/prop-types
  // @ts-ignore
  (
    {
      isRushIcon = false,
      isCRIcon = false,
      crData,
      renderCRIcon,
      indeterminate,
      ...rest
    },
    ref
  ) => {
    const defaultRef = React.useRef(undefined);
    const resolvedRef = ref || defaultRef;
    const inputRest: any = rest;
    // eslint-disable-next-line prettier/prettier
    inputRest.checked = inputRest.disabled ? false : (rest["checked"] as any);
    React.useEffect(() => {
      // @ts-ignore
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    // @ts-ignore
    return (
      <>
        <input
          type="checkbox"
          data-action={TableDataAction.SELECTION}
          ref={resolvedRef}
          // @ts-ignore
          onClick={rest.onClick}
          {...inputRest}
        />
        {isRushIcon && (
          <img
            style={{
              width: 24,
              height: 24,
              paddingLeft: "7px",
              paddingBottom: "3px",
            }}
            src={RushIcon}
            alt="Rush"
            title="Rush"
          />
        )}
        {isCRIcon && <>{renderCRIcon && renderCRIcon(crData)}</>}
      </>
    );
  }
);

export const Table: FC<TableData> = ({
  skipCheckboxes,
  columns,
  checkboxes,
  disabledRadios,
  resizable,
  data,
  sortKeys,
  sortable,
  hidable,
  rowActions,
  noDataMessage,
  loading,
  loadingText = "Loading",
  tableClassName = "table table-inline",
  isDragNDrop = true,
  fixed = false,
  tableRef,
  headerStyleNormal = false,
  isShowResult = true,
  orderable = false,
  showHeader = true,
  expandableRow,
  colsHide = [],
  showExpandableIcon,
  expandableComponent,
  onHandleSetGroupRadio,
  onClickHeader,
  onResizedChange,
  onSortedChange,
  onChangeIndex,
  onHideCols,
  onResetCols,
  getAllSelection,
  onDragRow,
  getRowId,
  setSelectedRows,
  getRowProps,
  dateTimeFormat = "yyyy-MM-dd HH:ss",
  locks = [],
  isMultiSortEvent = false,
  outerWidth,
  drop = "down",
  sticky = false,
  isDark,
  noWrapHeaderName,
  deleted,
  highlightRowIds = [],
  tableNoHeader = false,
  preventSelect = false, // prevent row active select.
  allowSelectWithGroup,
  groupLable,
  hasRushInData,
  expandedRows,
  setExpandedRows,
  renderCRIcon,
  tableContentMaxHeight,
  openLink,
  copyLink,
  resetAndHideSortedColumn,
  sections,
  getRowSection,
}): ReactElement => {
  const [contextMenu, setContextMenu] = useState(null);
  const menuRef = useRef(null);
  const [, setEffect] = useState({});
  const [copied, setCopied] = useState(false);
  const wrapperTableContentRef = useRef<HTMLDivElement>(null);
  const [selectedRowClicked, setSelectedRowClicked] = useState(null);
  const [willScrollable, setWillScrollable] = useState(sticky);
  const [xDragging, setXDragging] = useState(0);
  const [currentWidthDrag, setCurrentWidthDrag] = useState(170);
  const [rowIndexed, setRowIndexed] = useState("");
  const isInitialMount = useRef(true);
  const [groupRadio, setGroupRadio] = useState<
    Array<{ value: string; name: string; header?: string; disabled?: boolean }>
  >(checkboxes || []);

  const [tableColumns, setTableColumns] = useState<Column[] | unknown[]>([]);

  const toggleExpandableRow = (rowIndex: number) => {
    const newExpandedRowState = [...(expandedRows ?? [])];
    newExpandedRowState[rowIndex] = !newExpandedRowState[rowIndex];
    setExpandedRows?.(newExpandedRowState);
  };

  const tableDataWithRush = useMemo(() => {
    return data?.map((row) => ({
      ...row,
      showRushIcon: hasRushInData && row?.rush,
      showCRIcon: renderCRIcon && row?.criticalResult,
      crEntity: renderCRIcon ? row?.criticalResult : undefined,
    }));
  }, [data, hasRushInData, renderCRIcon]);

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // @ts-ignore
    selectedFlatRows,
    // @ts-ignore
    state: { columnResizing, selectedRowIds, sortBy },
    // @ts-ignore
    toggleRowSelected,
    // @ts-ignore
    toggleAllRowsSelected,
    setSortBy,
  } = useTable(
    {
      // @ts-ignore
      columns: tableColumns,
      // @ts-ignore
      getRowId,
      // @ts-ignore
      initialState: {
        // @ts-ignore
        hiddenColumns: columns
          // @ts-ignore
          .filter((column) => !column?.show)
          // @ts-ignore
          .map((column) => column?.id),
        // @ts-ignore
        sortBy: sortable ? sortKeys : [],
      },
      // @ts-ignore
      data: tableDataWithRush,
      manualSortBy: true,
      isMultiSortEvent: () => isMultiSortEvent,
    },
    useFlexLayout,
    useResizeColumns,
    useSortBy,
    useRowSelect,
    (hooks) => {
      // eslint-disable-next-line no-shadow
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      getAllSelection &&
        // eslint-disable-next-line @typescript-eslint/no-shadow
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: "selection",
            width: hasRushInData || renderCRIcon ? 65 : 30,
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            // eslint-disable-next-line react/prop-types
            // @ts-ignore
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div data-action={TableDataAction.SELECTION}>
                {/* @ts-ignore */}
                <IndeterminateCheckbox
                  data-action={TableDataAction.SELECTION}
                  {...getToggleAllRowsSelectedProps()}
                />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            // eslint-disable-next-line react/prop-types
            // @ts-ignore
            Cell: ({ row }) => {
              return (
                <div data-action={TableDataAction.SELECTION}>
                  {/* eslint-disable-next-line react/prop-types */}
                  <>
                    {/* @ts-ignore */}
                    <IndeterminateCheckbox
                      // @ts-ignore
                      // eslint-disable-next-line @typescript-eslint/no-use-before-define
                      isRushIcon={row?.original?.showRushIcon}
                      isCRIcon={row?.original?.showCRIcon}
                      crData={row?.original?.crEntity}
                      renderCRIcon={renderCRIcon}
                      disabled={
                        // for deletedAt of studies table or we can put disabled in data row.
                        // @ts-ignore
                        row.original.disabled ||
                        row.original.deletedAt ||
                        (skipCheckboxes || []).includes(row?.original?.id)
                      }
                      {...row.getToggleRowSelectedProps({
                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        onChange:
                          allowSelectWithGroup && groupLable
                            ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
                              () => onClick(row)
                            : row.getToggleRowSelectedProps().onChange,
                      })}
                    />
                  </>
                </div>
              );
            },
          },
          ...columns,
        ]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      rowActions &&
        // eslint-disable-next-line @typescript-eslint/no-shadow
        hooks.visibleColumns.push((columns) => [
          ...columns,
          {
            id: "action",
            width: rowActions.length * 25,
            Header: () => <div></div>,
            // eslint-disable-next-line react/prop-types
            // @ts-ignore
            Cell: ({ row }) => {
              return (
                <div className="list-icons text-right" data-action="action">
                  {rowActions.map((action: any) => {
                    return action(row);
                  })}
                </div>
              );
            },
          },
        ]);
    }
  );

  const onClick = (clickedRow: any) => {
    setSelectedRowClicked(clickedRow);
  };

  useImperativeHandle(tableRef, () => ({
    clearAllCheckBox() {
      toggleAllRowsSelected(false);
    },
    async toggleExpandableRowIndex(preCheck: any, index: number) {
      await preCheck(expandedRows);
      toggleExpandableRow(index);
    },
  }));

  /**
   * Functions.
   * @param event
   */
  // Function handle radio checkbox in studyvalidation table.
  const handleCheckboxesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, name } = event.target;

    const cloneGroupRadio = [...groupRadio];
    cloneGroupRadio?.forEach((item) => {
      if (item.header === name) {
        item.value = value;
      }
    });
    setGroupRadio(cloneGroupRadio);
    if (onHandleSetGroupRadio) {
      onHandleSetGroupRadio(cloneGroupRadio);
    }
  };

  const rowProps = (row: any) => {
    if (getRowProps) {
      return getRowProps(row, {
        toggleRowSelected,
        toggleAllRowsSelected,
      });
    }
    return row;
  };

  const tableStyles = tableContentMaxHeight
    ? {
        maxHeight: `calc(100vh - ${tableContentMaxHeight}px)`,
        overflow: "auto",
      }
    : {};

  // Expand row function.
  const renderExpanableIcon = (row: any, rowIndex: number) => {
    if (expandableRow) {
      const showIcon = showExpandableIcon && showExpandableIcon(row);
      const icon = expandedRows?.[rowIndex]
        ? "fa fa-minus-square"
        : "fa fa-plus-square";
      return showIcon ? (
        <div className="mr-1">
          <i
            className={icon}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandableRow(rowIndex);
            }}
            aria-hidden="true"
          />
        </div>
      ) : null;
    }
    return null;
  };

  const renderExpandableRow = (row: any, rowIndex: number) => {
    return (
      expandableRow &&
      expandedRows?.[rowIndex] &&
      expandableComponent &&
      expandableComponent(row)
    );
  };
  /**
   * Render sort icon.
   */

  const sortIcon = isDark ? SortDarkIcon : SortLightIcon;
  const sortDescIcon = isDark ? SortDarkDescIcon : SortLightDescIcon;
  const sortAcsIcon = isDark ? SortDarkAcsIcon : SortLightAcsIcon;

  const shouldShowSortIndex =
    (sortKeys ?? [])?.filter((s) => s.sortIndex ?? 0 > 0)?.length > 1;

  const renderImageIcon = (icon: string, style?: CSSProperties) => {
    return <img src={icon} style={style} />;
  };

  const renderSortIcon = useCallback(
    (column: Column): React.ReactElement => {
      const hasKey = sortBy.map((s) => s.id).includes(column.id as string);

      if (!hasKey)
        // @ts-ignore
        return column?.canSort ? (
          <span className="cursor-pointer">{renderImageIcon(sortIcon)}</span>
        ) : (
          <span></span>
        );
      const isSortedDesc = sortBy.find((s) => s.id === column.id)?.desc;
      const sortIndex = sortKeys?.find((x) => x.id === column?.id)?.sortIndex;
      if (isSortedDesc === false)
        return (
          <span className="cursor-pointer">
            {renderImageIcon(sortAcsIcon)}
            {shouldShowSortIndex && (
              <span className="text-sort-ic">{sortIndex}</span>
            )}
          </span>
        );
      if (isSortedDesc === true)
        return (
          <span className="cursor-pointer">
            {renderImageIcon(
              sortDescIcon,
              isDark ? { marginBottom: 2 } : { marginBottom: 1 }
            )}
            {shouldShowSortIndex && (
              <span className="text-sort-ic">{sortIndex}</span>
            )}
          </span>
        );
      return (
        <span className="cursor-pointer">{renderImageIcon(sortIcon)}</span>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortBy, columns]
  );
  // Drop column.
  const handleDroppedCol = (droppedId: string, index: number) => {
    // the table have action will increase index 1. that why we should minus one when insert new column.
    // @ts-ignore
    const hasAction = !!tableColumns.find((x) => x.id === "action");
    const columnToMove = tableColumns.find(
      // @ts-ignore
      (x) => x.orderId === droppedId
    );
    const newTs: Column[] | unknown[] = tableColumns.filter(
      // @ts-ignore
      (c) => c.orderId !== droppedId
    );
    newTs.splice(hasAction ? index - 1 : index, 0, columnToMove);
    const reindexTableColumns = newTs.map((i, idx) => {
      return {
        // @ts-ignore
        ...i,
        index: idx,
      };
    });
    setTableColumns(reindexTableColumns);
    if (onChangeIndex) {
      onChangeIndex(reindexTableColumns);
    }
    setRowIndexed("");
    setXDragging(0);
    setCurrentWidthDrag(170);
  };
  // Draping column.
  const handleMovingCol = (
    xPosition: number,
    colWidth: number,
    colId: string
  ) => {
    setXDragging(xPosition - colWidth);
    setCurrentWidthDrag(
      // @ts-ignore
      tableColumns.find((x) => x.id === colId)
    );
  };

  /**
   * Handle stick table.
   * @param stick
   * @returns
   */

  // build class name for stick selection and action.
  const actionClassName = (id: string) => {
    return `${
      (id === "selection" || id === "action") &&
      sticky &&
      willScrollable &&
      (data ?? []).length > 0
        ? `sticky text-center ${
            id === "selection" ? "left" : "right"
          }-0 border-${id === "selection" ? "right" : "left"}-l ${
            isDark ? "dark" : "light"
          }`
        : ""
    }`;
  };

  /**
   * Effects
   */

  useEffect(() => {
    if (!orderable) {
      // @ts-ignore
      setTableColumns(columns);
    } else {
      if (hidable) {
        // @ts-ignore
        const allowCols = [];
        columns?.forEach((cl) => {
          // @ts-ignore
          allowCols.push({ ...cl, orderId: uuid() });
        });
        // @ts-ignore
        setTableColumns(allowCols);
      } else {
        // @ts-ignore
        setTableColumns(columns);
      }
    }
  }, [orderable, columns, colsHide, hidable, hasRushInData, renderCRIcon]);

  // Listen for column resizing
  useEffect(() => {
    if (!isInitialMount.current && columnResizing.isResizingColumn === null) {
      const target = columnResizing.columnWidths;
      // @ts-ignore
      if (onResizedChange) onResizedChange(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnResizing]);

  useEffect(() => {
    if (hasRushInData || renderCRIcon) setEffect({}); // TRIGGER RE-RENDER
  }, [hasRushInData, renderCRIcon]);

  useEffect(() => {
    if (!isInitialMount.current && !loading) {
      if (setSelectedRows) {
        setSelectedRows(
          selectedFlatRows
            .map((row: any) => row.original)
            .filter((item: any) => !item?.disabled)
            .filter((item) => {
              if (!skipCheckboxes) return true;
              return !skipCheckboxes.includes(item?.id);
            })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelectedRows, selectedFlatRows, loading]);

  // handle select with group
  useEffect(() => {
    if (
      allowSelectWithGroup &&
      groupLable &&
      rows.length > 0 &&
      selectedRowClicked
    ) {
      // // unique id
      // @ts-ignore
      const groupId = selectedRowClicked?.original[groupLable];
      rows.forEach((row) => {
        if (
          groupId !== "" &&
          row.original[groupLable] === groupId &&
          // @ts-ignore
          selectedRowClicked?.id !== row.id
        ) {
          // @ts-ignore
          toggleRowSelected(row.id, selectedRowClicked?.isSelected);
        }
      });
      setSelectedRowClicked(null);
    }
  }, [
    rows,
    allowSelectWithGroup,
    groupLable,
    toggleRowSelected,
    selectedRowClicked,
  ]);

  useEffect(() => {
    if (!isInitialMount.current) {
      // Dispatch to container sort keys of column to request params.
      if (onSortedChange) {
        onSortedChange([...sortBy] as TableSortKey[]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  // Init sort keys.
  useEffect(() => {
    if (sortable && sortKeys) {
      // @ts-ignore
      setSortBy(sortKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKeys, sortable]);

  useEffect(() => {
    if (!isInitialMount.current) {
      if (getAllSelection) getAllSelection(selectedRowIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIds]);

  // Mark as mounted when data changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [data]);

  /**
   * Will scrollable or not depend on width of table content.
   * Previously this was only re-checked on window resize or when `contentWidth`/`data`
   * changed, so resizing/hiding/reordering columns (or column settings loading after data)
   * left the sticky state stale. A ResizeObserver on the wrapper (container size: window,
   * sidebars) and on the <table> (content size: column widths/visibility) covers every case.
   * It compares the wrapper's own scrollWidth/clientWidth, so the old `contentWidth` prop was removed.
   */
  useEffect(() => {
    const wrapper = wrapperTableContentRef.current;
    // ResizeObserver is missing in jsdom (Vitest); skip sticky detection there instead of crashing.
    if (!sticky || !wrapper || typeof ResizeObserver === "undefined")
      return undefined;

    const checkScrollable = () =>
      setWillScrollable(wrapper.scrollWidth - wrapper.clientWidth > 0);

    const observer = new ResizeObserver(checkScrollable);
    observer.observe(wrapper);
    const tableElement = wrapper.querySelector("table");
    if (tableElement) observer.observe(tableElement);
    checkScrollable();

    return () => observer.disconnect();
  }, [sticky]);

  const tableClassNameValue = useMemo(() => {
    // @ts-ignore
    const hasAction = !!tableColumns.find((x) => x?.id === "action");
    if (!hasAction) return `${tableClassName} table-last-child-flex-start`;
    return tableClassName;
  }, [tableColumns, tableClassName]);

  /**
   * Memo Functions.
   */

  /**
   * Wrapper class dynamic.
   * Table will fixed or responsive.
   */

  const tableWrapperClassName = useMemo(() => {
    return fixed ? "table-fixed" : "table-responsive";
  }, [fixed]);

  // cell status.
  const getCellDeleted = useCallback(
    (id: string) => {
      return deleted?.includes(id);
    },
    [deleted]
  );

  const getCellLocked = useCallback(
    (id: string) => {
      return locks?.map((x) => x.resourceId).includes(id);
    },
    [locks]
  );

  const getCellClassName = useCallback(
    (
      id: string,
      rowCellProps: TableRowProps,
      locked: boolean,
      cellDeleted: boolean
    ) => {
      // @ts-ignore
      let flatRows = selectedFlatRows.map((x) => x.original?.id);
      if (highlightRowIds.length > 0)
        flatRows = [...flatRows, ...highlightRowIds];

      const className = rowCellProps?.className;
      const cellHighlightClass =
        !locked && !cellDeleted ? "cell-highlight" : "";

      const highLightClass = flatRows.includes(id)
        ? `${preventSelect ? "" : "cell-selected"} ${cellHighlightClass} ${
            isDark ? "dark" : ""
          }`
        : ` ${cellHighlightClass} ${isDark ? "dark" : ""}`;
      const cellClass = className
        ? `${highLightClass} ${className}`
        : highLightClass;
      return cellClass;
    },
    [isDark, selectedFlatRows, highlightRowIds, preventSelect]
  );

  // function will get data-action attributes and set for each td.
  // it help table can prevent click when select action and selection column.
  const getDataActionFormKey = (key: string) => {
    // get data-action
    let dataAction = TableDataAction.CELL; // normal action

    // selections
    if (key?.includes("_selection")) {
      dataAction = TableDataAction.SELECTION;
      // actions
    } else if (key?.includes("_action")) {
      dataAction = TableDataAction.ACTION;
    }
    return dataAction;
  };
  /* --------------------- MENU CONTEXT -----------------*/
  const handleContextMenu = useCallback(
    // @ts-ignore
    (event, row, cell) => {
      if (
        cell?.column?.id === "action" ||
        cell?.column?.id === "selection" ||
        locks?.map((x) => x.resourceId)?.includes(row?.original?.id)
      )
        return;

      if (!openLink || !copyLink) return;
      event.preventDefault();

      if (getCellDeleted(row.original.id)) return;

      const isRightSide = event.clientX > window.innerWidth / 2;

      setContextMenu({
        // @ts-ignore
        x: event.clientX,
        y: event.clientY,
        position: isRightSide ? "right" : "left",
        row,
      });
      // eslint-disable-next-line prettier/prettier
    },
    [copyLink, getCellDeleted, locks, openLink]
  );

  const openInNewTab = () => {
    // @ts-ignore
    openLink?.(contextMenu.row);
    setContextMenu(null);
  };

  const handleCopyLink = () => {
    setCopied(true);
    // @ts-ignore
    copyLink?.(contextMenu.row);
    setTimeout(() => {
      setContextMenu(null);
      setCopied(false);
    }, 500);
  };

  /* --------------------- SECTIONS -----------------*/
  const renderSectionHeader = (section: TableSection, count: number) => {
    const visibleColumnCount =
      headerGroups[0]?.headers.filter(
        (column) => !colsHide.includes(getKeyFromCol(column))
      ).length || 1;
    return (
      <tr
        key={`section-${section.key}`}
        className={`table-section-header ${isDark ? "dark" : ""}`}
      >
        <td className="table-section-header__cell" colSpan={visibleColumnCount}>
          <span className="d-flex align-items-center">
            <span className="table-section-header__title">{section.title}</span>
            {section.showCount !== false && count > 0 && (
              <span className="badge table-section-header__count m-l-xs">
                {count}
              </span>
            )}
          </span>
          {section.description && (
            <span className="table-section-header__description">
              {section.description}
            </span>
          )}
        </td>
      </tr>
    );
  };

  // group rows by section and keep the original index for expandable rows.
  const renderBodyRows = (
    renderRow: (row: Row<any>, rowIndex: number) => React.ReactNode
  ) => {
    if (!sections?.length || !getRowSection) return rows.map(renderRow);

    return sections.map((section) => {
      const sectionRows = rows
        .map((row, rowIndex) => ({ row, rowIndex }))
        .filter(({ row }) => getRowSection(row.original) === section.key);
      if (!sectionRows.length && section.hideWhenEmpty) return null;

      return (
        <React.Fragment key={section.key}>
          {renderSectionHeader(section, sectionRows.length)}
          {sectionRows.length > 0
            ? sectionRows.map(({ row, rowIndex }) => renderRow(row, rowIndex))
            : section.emptyMessage && (
                <tr className="table-section-header__empty">
                  <td className="w-100">{section.emptyMessage}</td>
                </tr>
              )}
        </React.Fragment>
      );
    });
  };

  useEffect(() => {
    // @ts-ignore
    const handleClickOutside = (event) => {
      // @ts-ignore
      if (menuRef.current && !menuRef.current?.contains(event.target)) {
        setContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`${tableWrapperClassName}`}
      style={tableStyles}
      ref={wrapperTableContentRef}
    >
      {contextMenu && (
        <ul
          ref={menuRef}
          className={`context-menu ${isDark ? "dark" : ""}`}
          style={{
            // @ts-ignore
            top: contextMenu?.y,
            // @ts-ignore
            ...(contextMenu.position === "right"
              ? // @ts-ignore
                { right: window.innerWidth - contextMenu.x }
              : // @ts-ignore
                { left: contextMenu.x }),
          }}
        >
          <li onClick={openInNewTab}>Open in new tab</li>
          <li onClick={handleCopyLink}>
            <span>Copy link address</span>{" "}
            <i
              className={`${!copied ? "icon-copy" : "icon-check2"} ${
                copied ? "text-success" : ""
              }`}
            />
          </li>
        </ul>
      )}
      {hidable && (
        <TableFilterModal
          isDark={isDark}
          onChangeIndex={(newColumns) => {
            // @ts-ignore
            const actionCol = columns.find((x) => x.id === "action");
            setTableColumns(
              actionCol ? [...newColumns, actionCol] : newColumns
            );
            // @ts-ignore
            onChangeIndex(newColumns);
          }}
          onHideCols={onHideCols}
          onResetCols={onResetCols}
          columns={tableColumns}
          isShowResult={isShowResult}
          drop={drop}
          colsHide={colsHide}
          sortKeys={sortKeys as TableSortKey[]}
          resetAndHideSortedColumn={resetAndHideSortedColumn}
        />
      )}

      <table
        {...getTableProps()}
        className={tableClassNameValue}
        style={{ borderTop: headerStyleNormal ? 0 : 1 }}
      >
        <thead
          className={tableNoHeader ? "d-none" : ""}
          style={{ display: showHeader ? "" : "none" }}
        >
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, index: number) => {
                if (colsHide.includes(getKeyFromCol(column))) return null;
                return (
                  <th
                    {...column.getHeaderProps()}
                    className={`${actionClassName(column.id)} ${
                      headerStyleNormal ? "" : "header-style"
                    } ${
                      column.isResizing
                        ? `isResizing ${isDark ? "dark" : "light"}`
                        : ""
                    } ${
                      resizable || orderable
                        ? "header-resize justify-content-between"
                        : ""
                    } ${hidable ? "thead-hide" : ""}`}
                  >
                    {!COLUMNS_IGNORE_IDS.includes(column.id) && (
                      <DropTarget
                        className="w-100"
                        hoverClass="hover-class-dark" // Header background when dropped.
                        onMoveX={(xTarget) => {
                          handleMovingCol(
                            xTarget,
                            column.width,
                            getKeyFromCol(column)
                          );
                        }}
                        onItemDropped={(droppedId: string) => {
                          handleDroppedCol(droppedId, index);
                        }}
                      >
                        <Draggable
                          className="d-flex w-100 align-items-between"
                          onDragging={() => {
                            setRowIndexed(column?.orderId);
                          }}
                          {...column.getHeaderProps()}
                          disableDrag={!orderable}
                          dataItem={column?.orderId}
                        >
                          <div
                            className="d-flex align-items-center w-100"
                            {...column.getSortByToggleProps()}
                          >
                            {checkboxes
                              ?.map((x) => x.header)
                              .includes(column.id) && (
                              <input
                                className="m-r-xxs"
                                name={column.id}
                                disabled={
                                  disabledRadios ||
                                  groupRadio.find((x) => x.header === column.id)
                                    ?.disabled
                                }
                                checked={
                                  groupRadio.find((x) => x.header === column.id)
                                    ?.value === "on"
                                }
                                type="radio"
                                onChange={handleCheckboxesChange}
                              />
                            )}
                            {noWrapHeaderName && column?.id !== "selection" ? (
                              <BigText
                                text={column.render("Header")}
                                maxWidth={column.width - 10}
                                className="flex-1"
                                minWidth={50}
                              />
                            ) : (
                              column.render("Header")
                            )}

                            {column["required"] && (
                              <span className="text-danger p-l-xxs">*</span>
                            )}
                            {column["isQuestion"] && (
                              <i
                                aria-hidden
                                onClick={() => {
                                  if (onClickHeader) {
                                    onClickHeader(column.getHeaderProps().key);
                                  }
                                }}
                                className="fa fa-question-circle cursor-pointer p-l-xxs"
                              />
                            )}

                            {sortable &&
                              !COLUMNS_IGNORE_IDS.includes(column.id) &&
                              column.sortable &&
                              renderSortIcon(column)}
                          </div>
                        </Draggable>
                      </DropTarget>
                    )}

                    {!COLUMNS_IGNORE_IDS.includes(column.id) &&
                      column.resizable &&
                      resizable && (
                        <>
                          <div
                            {...column.getResizerProps()}
                            className={`resize-handle ${
                              isDark ? "dark" : "light"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          ></div>
                        </>
                      )}
                    {COLUMNS_IGNORE_IDS.includes(column.id) &&
                      column.render("Header")}
                    {/* Use column.getResizerProps to hook up the events correctly */}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.length > 0 ? (
            renderBodyRows((row, rowIndex) => {
              prepareRow(row);
              // @ts-ignore
              const cellDeleted = getCellDeleted(row.original.id);
              // @ts-ignore
              const locked = getCellLocked(row.original.id);
              const cellProps = {
                ...row.getRowProps(rowProps(row)),
                class: getCellClassName(
                  row.original.id as string,
                  row.getRowProps(rowProps(row)),
                  locked,
                  cellDeleted as boolean
                ),
              };
              return isDragNDrop ? (
                <Draggable disableDrag={!onDragRow} dataItem={row?.id}>
                  {/* @ts-ignore */}
                  <DropTarget
                    hoverClass="no-class"
                    onItemDropped={(dropped: any) => {
                      if (onDragRow) {
                        onDragRow(dropped, row.id);
                      }
                    }}
                  >
                    <>
                      <tr {...cellProps}>
                        {row.cells.map((cell, cellIndex) => {
                          // @ts-ignore
                          const hideKeyId: string = getKeyFromCol(cell.column);
                          if (colsHide.includes(hideKeyId as string))
                            return null;
                          const moveStyle: CSSProperties =
                            xDragging > 0 &&
                            // @ts-ignore
                            cell?.column?.orderId === rowIndexed
                              ? getDragItemStyle(
                                  // @ts-ignore
                                  xDragging - outerWidth > 0
                                    ? // @ts-ignore
                                      xDragging - outerWidth
                                    : xDragging,
                                  currentWidthDrag,
                                  isDark
                                )
                              : {};
                          const rowRenderStyle = {
                            ...(cell.getCellProps().style as CSSProperties),
                          };
                          const rowStyle = { ...rowRenderStyle, ...moveStyle };
                          if (locked) {
                            return (
                              <LockRecord
                                lockString={getLockedString(
                                  locks,
                                  // @ts-ignore
                                  row.original.id,
                                  dateTimeFormat
                                )}
                              >
                                <td
                                  onContextMenu={(e) =>
                                    handleContextMenu(e, row, cell)
                                  }
                                  className={actionClassName(cell.column.id)}
                                  data-action={getDataActionFormKey(
                                    cell.getCellProps()?.key as string
                                  )}
                                  {...cell.getCellProps()}
                                >
                                  {cellIndex === 0 &&
                                    renderExpanableIcon(row, rowIndex)}
                                  {cell.render("Cell")}
                                </td>
                              </LockRecord>
                            );
                          }

                          return (
                            <td
                              onContextMenu={(e) =>
                                handleContextMenu(e, row, cell)
                              }
                              data-action={getDataActionFormKey(
                                cell.getCellProps()?.key as string
                              )}
                              {...cell.getCellProps()}
                              // Sticky selection/action cells: this drag-n-drop branch used to omit
                              // actionClassName, so a `sticky` table without `isDragNDrop={false}`
                              // only stuck its header. Keep in sync with the non-drag branch below.
                              className={actionClassName(cell.column.id)}
                              style={rowStyle}
                            >
                              {cellIndex === 0 &&
                                renderExpanableIcon(row, rowIndex)}
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                      {renderExpandableRow(row, rowIndex)}
                    </>
                  </DropTarget>
                </Draggable>
              ) : (
                <>
                  <tr {...cellProps}>
                    {row.cells.map((cell, cellIndex) => {
                      // get styling.
                      // @ts-ignore
                      const hideKeyId: string = getKeyFromCol(cell.column);
                      if (colsHide.includes(hideKeyId as string)) return null;
                      const moveStyle: CSSProperties =
                        xDragging > 0 &&
                        // @ts-ignore
                        cell?.column?.orderId === rowIndexed
                          ? getDragItemStyle(
                              // @ts-ignore
                              xDragging - outerWidth > 0
                                ? // @ts-ignore
                                  xDragging - outerWidth
                                : xDragging,
                              currentWidthDrag,
                              isDark
                            )
                          : {};
                      const rowRenderStyle = {
                        ...(cell.getCellProps().style as CSSProperties),
                      };
                      const rowStyle = { ...rowRenderStyle, ...moveStyle };

                      if (locked) {
                        return (
                          <LockRecord
                            lockString={getLockedString(
                              locks,
                              // @ts-ignore
                              row.original.id,
                              dateTimeFormat
                            )}
                          >
                            <td
                              onContextMenu={(e) =>
                                handleContextMenu(e, row, cell)
                              }
                              data-action={getDataActionFormKey(
                                cell.getCellProps()?.key as string
                              )}
                              {...cell.getCellProps()}
                              className={actionClassName(cell.column.id)}
                            >
                              {cellIndex === 0 &&
                                renderExpanableIcon(row, rowIndex)}
                              {cell.render("Cell")}
                            </td>
                          </LockRecord>
                        );
                      }

                      return (
                        <td
                          onContextMenu={(e) => handleContextMenu(e, row, cell)}
                          data-action={getDataActionFormKey(
                            cell.getCellProps()?.key as string
                          )}
                          {...cell.getCellProps()}
                          className={actionClassName(cell.column.id)}
                          style={rowStyle}
                        >
                          {cellIndex === 0 &&
                            renderExpanableIcon(row, rowIndex)}
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {renderExpandableRow(row, rowIndex)}
                </>
              );
            })
          ) : (
            <NoMessageData
              isShowResult={isShowResult}
              // @ts-ignore
              noDataMessage={noDataMessage}
            />
          )}
        </tbody>
      </table>
      {loading && <TableLoading loadingText={loadingText} loading={loading} />}
    </div>
  );
};
